/* Immersive product — Shopify build.
 *
 * Two jobs: run the gallery, and keep the variant selection honest. No
 * framework, no carousel library, no fetch. The live product page already
 * throws a JavaScript error in 52% of sessions and averages 1.5 errors per
 * session; anything added here has to earn its place against that.
 *
 * The gallery is a native scroll-snap track, so the browser owns the
 * momentum, the rubber-banding and the thumb-drag mapping — three things a
 * hand-rolled carousel gets subtly wrong on a phone. Script reads position
 * back; it never drives the scroll except on an explicit arrow or key.
 *
 * Variant data is rendered into the page as JSON by Liquid rather than
 * fetched, because the server already knew every price and availability at
 * render time. Picking a size should not cost a network round trip.
 */

(function () {
  "use strict";

  document.querySelectorAll(".cp").forEach(function (root) {
    if (root.dataset.cpReady) return;
    root.dataset.cpReady = "true";

    var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var hasIO = "IntersectionObserver" in window;

    function parse(selector, fallback) {
      var node = root.querySelector(selector);
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return fallback;
      }
    }

    var variants = parse("[data-variants]", []);
    var copy = parse("[data-copy]", {});

    /* ── Reveals ──────────────────────────────────────────────────────── */
    var reveals = root.querySelectorAll(".reveal");

    if (!hasIO || reduced) {
      reveals.forEach(function (node) {
        node.classList.add("is-in");
      });
    } else {
      var seen = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-in");
            seen.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
      );
      reveals.forEach(function (node, index) {
        node.style.transitionDelay = (index % 5) * 70 + "ms";
        seen.observe(node);
      });
    }

    /* ── The roll ─────────────────────────────────────────────────────── */
    var track = root.querySelector("[data-track]");
    var goTo = function () {};

    if (track) {
      var frames = Array.prototype.slice.call(track.querySelectorAll("[data-frame]"));
      var marks = root.querySelector("[data-marks]");
      var countEl = root.querySelector("[data-count]");
      var prev = root.querySelector("[data-prev]");
      var next = root.querySelector("[data-next]");
      var current = 0;

      if (marks) {
        frames.forEach(function (frame, index) {
          var button = document.createElement("button");
          button.type = "button";
          button.setAttribute("aria-label", "Go to image " + (index + 1));
          button.addEventListener("click", function () {
            goTo(index);
          });
          marks.appendChild(button);
        });
        marks.removeAttribute("aria-hidden");
      }

      function nearest() {
        var middle = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestGap = Infinity;
        frames.forEach(function (frame, index) {
          var gap = Math.abs(frame.offsetLeft + frame.offsetWidth / 2 - middle);
          if (gap < bestGap) {
            bestGap = gap;
            best = index;
          }
        });
        return best;
      }

      function paint() {
        current = nearest();
        frames.forEach(function (frame, index) {
          frame.classList.toggle("is-live", index === current);
        });
        if (marks) {
          Array.prototype.forEach.call(marks.children, function (mark, index) {
            mark.classList.toggle("is-live", index === current);
            mark.setAttribute("aria-current", index === current ? "true" : "false");
          });
        }
        if (countEl) countEl.textContent = String(current + 1);
        if (prev) prev.disabled = current === 0;
        if (next) next.disabled = current === frames.length - 1;
      }

      goTo = function (index) {
        var frame = frames[Math.max(0, Math.min(index, frames.length - 1))];
        if (!frame) return;
        track.scrollTo({
          left: frame.offsetLeft + frame.offsetWidth / 2 - track.clientWidth / 2,
          behavior: reduced ? "auto" : "smooth"
        });
      };

      var ticking = false;
      track.addEventListener(
        "scroll",
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            paint();
            ticking = false;
          });
        },
        { passive: true }
      );

      if (prev) prev.addEventListener("click", function () { goTo(current - 1); });
      if (next) next.addEventListener("click", function () { goTo(current + 1); });

      track.addEventListener("keydown", function (event) {
        if (event.key === "ArrowRight") { event.preventDefault(); goTo(current + 1); }
        if (event.key === "ArrowLeft") { event.preventDefault(); goTo(current - 1); }
        if (event.key === "Home") { event.preventDefault(); goTo(0); }
        if (event.key === "End") { event.preventDefault(); goTo(frames.length - 1); }
      });

      // Mouse drag only. Touch and trackpad already scroll natively and must
      // be left alone, or the page fights the thumb.
      var dragging = false;
      var startX = 0;
      var startScroll = 0;

      track.addEventListener("pointerdown", function (event) {
        if (event.pointerType !== "mouse") return;
        dragging = true;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        track.classList.add("is-dragging");
        track.setPointerCapture(event.pointerId);
      });

      track.addEventListener("pointermove", function (event) {
        if (!dragging) return;
        track.scrollLeft = startScroll - (event.clientX - startX);
      });

      function endDrag() {
        if (!dragging) return;
        dragging = false;
        track.classList.remove("is-dragging");
        goTo(nearest());
      }

      track.addEventListener("pointerup", endDrag);
      track.addEventListener("pointercancel", endDrag);

      // Images change every offsetLeft when they land, so the first true
      // paint has to wait for them or the counter starts on the wrong frame.
      window.addEventListener("load", paint);
      window.addEventListener("resize", paint);
      paint();

      /* Jumping to a variant's own image. A colour swatch that does not move
       * the gallery makes the shopper hunt for the colour they just picked. */
      root.cpShowMedia = function (mediaId) {
        if (!mediaId) return;
        for (var i = 0; i < frames.length; i += 1) {
          if (String(frames[i].dataset.mediaId) === String(mediaId)) {
            goTo(i);
            return;
          }
        }
      };
    }

    /* ── The dock ─────────────────────────────────────────────────────── */
    var dock = root.querySelector("[data-dock]");

    /* Watch the buy button itself, not the hero.
     *
     * Watching the hero meant the dock appeared the moment the first screen
     * scrolled by — while the real Add to bag button was still sitting in
     * view, so the page showed the same control twice. The dock exists for
     * when that button is gone, so the button is what it should follow.
     *
     * The 15% top margin grows the observation area upwards, which holds the
     * dock back until the button is a further 15% of a screen past the top
     * edge. Without it the dock would flick in the instant the button touched
     * the top of the viewport, which on a slow scroll reads as a glitch. */
    var buyButton = root.querySelector(".cta") || root.querySelector(".stage");
    var hero = root.querySelector(".stage");

    /* The dock also covers the stretch BEFORE the button. On a phone the
     * real Add to bag sits four screens down, under the gallery and the
     * pickers, and Clarity showed mobile visitors averaging 41% scroll depth:
     * most never saw a way to buy. So the dock shows whenever the hero has
     * gone and the real button is not on screen, on either side of it. */
    if (dock && buyButton && hasIO) {
      var heroGone = !hero;
      var buttonInView = false;
      var syncDock = function () {
        var show = heroGone && !buttonInView;
        dock.classList.toggle("is-up", show);
        dock.setAttribute("aria-hidden", show ? "false" : "true");
      };
      new IntersectionObserver(
        function (entries) {
          buttonInView = entries[0].isIntersecting;
          syncDock();
        },
        { rootMargin: "15% 0px 0px 0px", threshold: 0 }
      ).observe(buyButton);
      if (hero) {
        new IntersectionObserver(
          function (entries) {
            // Gone means scrolled up past the screen, not merely not yet loaded.
            heroGone = !entries[0].isIntersecting && entries[0].boundingClientRect.bottom < 0;
            syncDock();
          },
          { threshold: 0 }
        ).observe(hero);
      }
    }

    /* ── The offer countdown ──────────────────────────────────────────────
       A rolling window, held in a cookie so it survives a reload and a walk
       between pages rather than restarting at full every time — a timer that
       resets on refresh is visibly not a deadline.

       Cookie, not localStorage, so it is readable from any page on the
       storefront and expires on its own. Wrapped in try/catch throughout: a
       browser with cookies blocked must still get a working page, which it
       does — the clock simply starts from now for that visit. */
    var clock = root.querySelector("[data-clock]");

    if (clock) {
      var COOKIE = "cp_offer_ends";
      var windowMs = (Number(clock.dataset.hours) || 88) * 3600000;

      function readCookie(name) {
        try {
          var hit = document.cookie.match("(?:^|; )" + name + "=([^;]*)");
          return hit ? Number(decodeURIComponent(hit[1])) : NaN;
        } catch (error) {
          return NaN;
        }
      }

      function writeCookie(name, value, expiresAt) {
        try {
          document.cookie =
            name +
            "=" +
            encodeURIComponent(String(value)) +
            "; path=/; max-age=" +
            Math.ceil((expiresAt - Date.now()) / 1000) +
            "; SameSite=Lax";
        } catch (error) {
          /* Cookies blocked. The clock still runs for this page view. */
        }
      }

      function endsAt() {
        var stored = readCookie(COOKIE);
        // Missing, unreadable, already past, or further out than one whole
        // window (a stale cookie from a longer setting) — start a new one.
        if (!stored || isNaN(stored) || stored <= Date.now() || stored > Date.now() + windowMs) {
          var next = Date.now() + windowMs;
          writeCookie(COOKIE, next, next);
          return next;
        }
        return stored;
      }

      var target = endsAt();
      var d = clock.querySelector("[data-d]");
      var h = clock.querySelector("[data-h]");
      var m = clock.querySelector("[data-m]");
      var sec = clock.querySelector("[data-s]");

      function pad(value) {
        return value < 10 ? "0" + value : String(value);
      }

      function tick() {
        var left = target - Date.now();

        // Expired while the page was open: roll straight into the next
        // window rather than sitting at zero.
        if (left <= 0) {
          target = Date.now() + windowMs;
          writeCookie(COOKIE, target, target);
          left = windowMs;
        }

        var totalSeconds = Math.floor(left / 1000);
        if (d) d.textContent = pad(Math.floor(totalSeconds / 86400));
        if (h) h.textContent = pad(Math.floor((totalSeconds % 86400) / 3600));
        if (m) m.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        if (sec) sec.textContent = pad(totalSeconds % 60);

        clock.classList.toggle("is-final", left < 3600000);
      }

      tick();
      var ticker = window.setInterval(tick, 1000);

      // A backgrounded tab throttles its timers, so the clock drifts. Reading
      // the target again on return corrects it instead of counting the
      // seconds the browser skipped.
      document.addEventListener("visibilitychange", function () {
        if (!document.hidden) tick();
      });

      window.addEventListener("pagehide", function () {
        window.clearInterval(ticker);
      });
    }

    /* ── Variants ─────────────────────────────────────────────────────────
       Selection is held as an array of option values, one per option, in the
       product's own option order — so a product with one option or three
       works without special-casing either. */
    var pickers = Array.prototype.slice.call(root.querySelectorAll("[data-option-index]"));
    if (!pickers.length || !variants.length) return;

    var selection = [];
    pickers.forEach(function (picker) {
      var index = Number(picker.dataset.optionIndex);
      var on = picker.querySelector("button.is-on") || picker.querySelector("button");
      selection[index] = on ? on.dataset.value : null;
    });

    var idField = root.querySelector("[data-variant-id]");

    // Sizes now render with no pill selected, so the starting selection comes
    // from the variant the server matched, not from whichever pill is first
    // (which could be sold out and would grey the button before any choice).
    if (idField && idField.value) {
      var startVariant = variants.find(function (v) {
        return String(v.id) === String(idField.value);
      });
      if (startVariant && startVariant.options) {
        pickers.forEach(function (picker) {
          var index = Number(picker.dataset.optionIndex);
          if (startVariant.options[index] != null) selection[index] = startVariant.options[index];
        });
      }
    }
    // The price is stated twice — once in the hero ribbon, once in the buy
    // block — and both have to move together, or a shopper who scrolls back
    // up sees the price of a variant they no longer have selected.
    var priceEls = Array.prototype.slice.call(root.querySelectorAll("[data-price], [data-buy-price]"));
    var compareEls = Array.prototype.slice.call(root.querySelectorAll("[data-compare], [data-buy-compare]"));
    var savingEls = Array.prototype.slice.call(root.querySelectorAll("[data-saving], [data-buy-saving]"));
    var savePct = root.querySelector("[data-save-pct]");
    var ctaLabel = root.querySelector("[data-cta-label]");
    var ctaPrice = root.querySelector("[data-cta-price]");
    var dockPrice = root.querySelector("[data-dock-price]");
    var dockVariant = root.querySelector("[data-dock-variant]");
    var dockCta = root.querySelector("[data-dock-cta]");
    var clockAmount = root.querySelector("[data-clock-amount]");
    var submits = Array.prototype.slice.call(root.querySelectorAll(".cta"));
    if (dockCta) submits.push(dockCta);

    /* Has the shopper actually chosen, or are they looking at whatever
       Shopify happened to select first?

       This is the difference between a sale and a returned pair in the wrong
       size. The dock sits over the page from the moment the hero scrolls
       away, long before anyone has reached the size pills, and a submit there
       silently added the first available variant. */
    var picked = {};
    pickers.forEach(function (picker) {
      // A product with one value for an option has nothing to choose.
      // Colour arrives already selected and shown as selected (it matches the
      // photos), so it counts as chosen; only the size has to be asked for.
      picked[picker.dataset.optionIndex] =
        picker.querySelectorAll("button").length < 2 || picker.dataset.optionAsk === "color";
    });

    /* Whether an option's current value should show as chosen. A size the
       shopper has not tapped stays blank everywhere (pills, legend, dock),
       even though a variant is still matched behind it for price and stock. */
    function isShown(index) {
      var picker = pickers.find(function (node) {
        return Number(node.dataset.optionIndex) === index;
      });
      return !picker || picked[index] || picker.dataset.optionAsk !== "size";
    }

    function allPicked() {
      return Object.keys(picked).every(function (key) {
        return picked[key];
      });
    }

    if (dockCta) {
      /* The theme puts its loading state on the button inside the form, which
       * by the time the dock is showing has been scrolled past. Without this
       * the tap that matters most on a phone looks like it did nothing. */
      var dockLabel = dockCta.textContent;

      /* What the dock should read right now.
       *
       * It used to say "Add to bag" whatever the state, so tapping it with no
       * size chosen scrolled back up to the picker — which, since the picker
       * sits near the top of the page, felt like being thrown to the top for
       * no reason. Saying "Select size" makes the scroll the answer to a
       * question the button asked. */
      /* Names what is still missing: "Select size", "Select color", or
       * "Select size & color" when neither has been chosen. Options with a
       * single value never count, since there is nothing to choose. */
      var dockIdleLabel = function () {
        if (allPicked()) return dockLabel;
        var missing = pickers
          .filter(function (picker) {
            return !picked[picker.dataset.optionIndex];
          })
          .map(function (picker) {
            return picker.dataset.optionAsk || "option";
          })
          .sort(function (a, b) {
            return (a === "size" ? 0 : 1) - (b === "size" ? 0 : 1);
          });
        if (missing.length === 1 && missing[0] === "size") return copy.selectSize || "Select size";
        return "Select " + missing.join(" & ");
      };
      root.cpRefreshDock = function () {
        if (dockCta.textContent === (copy.adding || "Adding…")) return;
        // Disabled here means the chosen combination cannot be bought.
        dockCta.textContent = dockCta.disabled ? copy.soldOut || "Sold out" : dockIdleLabel();
      };

      var dockSettled = function () {
        dockCta.disabled = false;
        dockCta.textContent = dockIdleLabel();
      };
      document.addEventListener("ajaxProduct:added", dockSettled);
      dockSettled();

      dockCta.addEventListener("click", function () {
        if (allPicked()) {
          var form = root.querySelector("#cp-form");
          if (!form) return;

          dockCta.disabled = true;
          dockCta.textContent = copy.adding || "Adding…";
          // Restored on the event above; this is the backstop for a failed add
          // that never fires one.
          window.setTimeout(dockSettled, 6000);

          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
          return;
        }

        // Not chosen yet: take them to the control instead of guessing.
        var first = pickers.find(function (picker) {
          return !picked[picker.dataset.optionIndex];
        });
        if (!first) return;

        first.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
        first.classList.add("is-asking");
        window.setTimeout(function () {
          first.classList.remove("is-asking");
        }, 1800);
        var firstButton = first.querySelector("button");
        if (firstButton) firstButton.focus({ preventScroll: true });
      });
    }

    function match() {
      for (var i = 0; i < variants.length; i += 1) {
        var options = variants[i].options || [];
        var same = true;
        for (var j = 0; j < selection.length; j += 1) {
          if (selection[j] != null && options[j] !== selection[j]) {
            same = false;
            break;
          }
        }
        if (same) return variants[i];
      }
      return null;
    }

    /* What a chosen option should be called on screen.
     *
     * The pill's `data-value` is the raw Shopify option — often a bare "40" —
     * while the pill *shows* the expanded "US7/UK6/EU40" that Liquid built for
     * it. Reading the value gave the dock and the legend a naked EU number
     * that meant nothing to the three quarters of this store's traffic that is
     * not in Europe, and contradicted the pill directly above it.
     *
     * So the label is read off the button that renders it. One source, so the
     * two can never disagree, and no size conversion is duplicated here. */
    function labelFor(optionIndex, value) {
      if (!value) return "";

      var picker = pickers.find(function (node) {
        return Number(node.dataset.optionIndex) === Number(optionIndex);
      });
      if (!picker) return value;

      // Compared rather than selected: an option value can contain quotes and
      // slashes ("US7/UK6/EU40" is one of this store's own), and building a
      // selector out of it is a needless way to break.
      var buttons = Array.prototype.slice.call(picker.querySelectorAll("button"));
      for (var i = 0; i < buttons.length; i += 1) {
        if (buttons[i].dataset.value !== value) continue;
        var text = (buttons[i].textContent || "").trim();
        return text || value;
      }

      return value;
    }

    function apply() {
      var variant = match();

      pickers.forEach(function (picker) {
        var shown = isShown(Number(picker.dataset.optionIndex));
        picker.querySelectorAll("button").forEach(function (button) {
          var index = Number(picker.dataset.optionIndex);
          var on = shown && button.dataset.value === selection[index];
          button.classList.toggle("is-on", on);
          button.setAttribute("aria-checked", String(on));
        });

        var chosen = root.querySelector(
          '[data-chosen="' + picker.dataset.optionIndex + '"]'
        );
        var index = Number(picker.dataset.optionIndex);
        if (chosen) chosen.textContent = shown ? labelFor(index, selection[index]) : "";
      });

      // A combination that does not exist is stated, not silently ignored —
      // the alternative is a button that looks live and fails on click.
      if (!variant) {
        if (idField) idField.value = "";
        if (ctaLabel) ctaLabel.textContent = copy.unavailable || "Unavailable";
        submits.forEach(function (node) { node.disabled = true; });
        return;
      }

      if (idField) idField.value = variant.id;
      priceEls.forEach(function (node) { node.textContent = variant.price; });
      if (ctaPrice) ctaPrice.textContent = variant.price;
      if (dockPrice) dockPrice.textContent = variant.price;
      if (dockVariant) {
        // The variant title is the raw options joined; rebuild it from what
        // the pills actually show.
        var shown = (variant.options || [])
          .map(function (value, index) { return isShown(index) ? labelFor(index, value) : ""; })
          .filter(Boolean)
          .join(" · ");
        dockVariant.textContent = shown ? "· " + shown : "";
      }

      compareEls.forEach(function (node) {
        node.textContent = variant.compare;
        node.hidden = !variant.onSale;
      });
      savingEls.forEach(function (node) {
        var label = (copy.savingLabel || "You keep") + " " + variant.saving;
        // The ribbon chip carries the percentage too; the buy line does not.
        if (node.hasAttribute("data-saving") && variant.savePct) {
          label += " (" + variant.savePct + "%)";
        }
        node.textContent = label;
        node.hidden = !variant.onSale;
      });

      // The whole ribbon offer block goes when a variant is not discounted —
      // a countdown beside a full-price item counts down to nothing.
      var offerClock = root.querySelector(".offer-clock");
      if (offerClock) offerClock.hidden = !variant.onSale;

      submits.forEach(function (node) { node.disabled = !variant.available; });
      if (ctaLabel) {
        ctaLabel.textContent = variant.available
          ? copy.addToBag || "Add to bag"
          : copy.soldOut || "Sold out";
      }

      // The figure in that sentence has to follow the variant. A saving
      // quoted for a size the shopper is no longer looking at is just a
      // wrong number sitting next to a countdown.
      if (clockAmount) {
        clockAmount.textContent =
          clockAmount.dataset.amountSource === "compare" ? variant.compare : variant.saving;
      }

      if (variant.mediaId && root.cpShowMedia) root.cpShowMedia(variant.mediaId);

      // Keep the address bar honest, so a shared link opens on the same
      // variant. replaceState, not pushState — picking a size is not a page
      // the back button should have to walk through.
      try {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
      } catch (error) {
        /* A blocked history API must not stop the size from being picked. */
      }
    }

    pickers.forEach(function (picker) {
      var index = Number(picker.dataset.optionIndex);

      picker.addEventListener("click", function (event) {
        var button = event.target.closest("button");
        if (!button || !picker.contains(button)) return;
        selection[index] = button.dataset.value;
        picked[index] = true;
        apply();
        // The dock label was only set on load, so it kept saying "Select size"
        // after a size had been chosen.
        if (root.cpRefreshDock) root.cpRefreshDock();
      });

      picker.addEventListener("keydown", function (event) {
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
        var buttons = Array.prototype.slice.call(picker.querySelectorAll("button"));
        var at = buttons.indexOf(document.activeElement);
        if (at === -1) return;
        event.preventDefault();
        var step = event.key === "ArrowRight" ? 1 : -1;
        var target = buttons[(at + step + buttons.length) % buttons.length];
        target.focus();
        target.click();
      });
    });

    /* Add to cart without throwing the page away.
     *
     * The theme already knows how to do this, and doing it ourselves was the
     * bug: `theme.CartDrawer` listens on `document` for `ajaxProduct:added`
     * and responds with `buildCart()` then `open()`. We were dispatching
     * `cart:updated`, which only a separate snippet listens for — that snippet
     * adds the open class and refreshes the totals but never fills the item
     * list. The drawer slid open empty, our own refresh raced the theme's, and
     * the failure path then sent the shopper to the cart page on top of it.
     *
     * So hand the form to `theme.ProductForm`, which is the same class the
     * theme's own product pages use. It posts to the cart, shows the loading
     * state on the button, reports a sold-out variant, and fires the event the
     * drawer is waiting for. Nothing here reimplements any of it.
     */
    function cartRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    }

    function wireCart() {
      var form = root.querySelector("#cp-form");
      if (!form) return;

      // The theme's own handler. It calls preventDefault itself, so the form
      // never navigates, and it owns every state the button can be in.
      if (window.theme && typeof window.theme.ProductForm === "function") {
        try {
          new window.theme.ProductForm(form, ".cta");
          return;
        } catch (error) {
          // Fall through to the standalone path below rather than leaving the
          // button with no handler at all.
        }
      }

      /* No theme to lean on — the standalone build, or a theme that has
       * changed shape. Add over fetch and then fire the same event the drawer
       * listens for, so the theme still does the building if it is there. A
       * failure ends in a normal form post, which is the behaviour the browser
       * would have given us anyway. */
      form.addEventListener("submit", function (event) {
        if (event.defaultPrevented) return;
        event.preventDefault();

        // Sizes start unselected, so the in-page button must not add a size
        // nobody chose: send the shopper to the missing picker instead.
        if (typeof allPicked === "function" && !allPicked()) {
          var missing = pickers.find(function (picker) {
            return !picked[picker.dataset.optionIndex];
          });
          if (missing) {
            missing.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
            missing.classList.add("is-asking");
            window.setTimeout(function () {
              missing.classList.remove("is-asking");
            }, 1800);
          }
          return;
        }

        var button = form.querySelector('button[type="submit"]');
        var label = button && button.querySelector("[data-cta-label]");
        var wasLabel = label && label.textContent;
        if (button) button.disabled = true;
        if (label) label.textContent = copy.adding || "Adding…";

        var restore = function () {
          if (button) button.disabled = false;
          if (label && wasLabel != null) label.textContent = wasLabel;
        };

        fetch(cartRoot() + "cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          body: new FormData(form)
        })
          .then(function (response) {
            return response.json().then(function (body) {
              return { ok: response.ok, body: body };
            });
          })
          .then(function (result) {
            restore();

            if (!result.ok) {
              // Shopify explains the refusal better than we can — usually that
              // the variant just sold out. Say it on the button rather than
              // navigating away to show it.
              if (label) {
                label.textContent =
                  (result.body && (result.body.description || result.body.message)) ||
                  copy.unavailable ||
                  "Unavailable";
                window.setTimeout(function () {
                  if (wasLabel != null) label.textContent = wasLabel;
                }, 3200);
              }
              return;
            }

            // What the theme's own product form fires. The cart drawer builds
            // its contents and opens itself in response.
            form.dispatchEvent(
              new CustomEvent("ajaxProduct:added", {
                detail: { product: result.body, addToCartBtn: button },
                bubbles: true
              })
            );

            // Nothing is listening, so there is no drawer to show. Send them
            // to the cart rather than leaving the tap looking ignored.
            if (!document.getElementById("CartDrawer")) {
              window.location.href = cartRoot() + "cart";
            }
          })
          .catch(function () {
            // Network gone or blocked. Hand it back to the browser, which will
            // post the form the old way.
            restore();
            form.submit();
          });
      });
    }

    wireCart();
    apply();
  });
})();

/* ── Lightbox ─────────────────────────────────────────────────────────────
 *
 * Tap or click a gallery image (or the hero) to see it large, on the same
 * page, in a native <dialog>: it sits above the header, the dock and every
 * app widget, traps focus, and closes on Escape without any of that being
 * written here.
 *
 * Zoom, one model for every input: the image carries
 * translate(tx, ty) scale(s) with its origin at the top left, so zooming
 * about any point is "keep that point where it is", and panning is just
 * moving tx/ty inside limits that never show an edge of empty space.
 *   - Mouse: click zooms in at the cursor, the image then follows the
 *     cursor, click again zooms out. The wheel zooms about the cursor.
 *   - Touch: pinch to zoom, drag to pan when zoomed, double tap to zoom in
 *     or out. Unzoomed, swipe sideways for the next image and down to close.
 *
 * Nothing is fetched to open it: the image already on the page (in the
 * browser's cache) shows at once, and the 2400px version replaces it when it
 * has loaded, so opening never waits and never flashes empty. The two
 * neighbours are fetched in the background so the next swipe is instant too.
 */
(function () {
  "use strict";

  var MAX = 4;
  var ZOOM = 2.5;

  function bigSrc(img) {
    var url = img.currentSrc || img.src || "";
    if (!url) return url;
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, "$1width=2400");
    return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=2400";
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  document.querySelectorAll(".cp").forEach(function (root) {
    if (root.dataset.cpLightbox) return;
    var frames = Array.prototype.slice.call(root.querySelectorAll(".roll [data-frame] img"));
    var hero = root.querySelector(".stage__shot");
    if (!frames.length && !hero) return;
    if (typeof HTMLDialogElement !== "function") return;
    root.dataset.cpLightbox = "true";

    var sources = frames.length ? frames : [hero];

    /* ── Markup ── */
    var dialog = document.createElement("dialog");
    dialog.className = "cp-lb";
    dialog.setAttribute("aria-label", "Image viewer");
    dialog.innerHTML =
      '<div class="cp-lb__stage" data-lb-stage>' +
      '<img class="cp-lb__img" data-lb-img alt="" draggable="false">' +
      "</div>" +
      '<div class="cp-lb__bar">' +
      '<span class="cp-lb__count" data-lb-count></span>' +
      '<span class="cp-lb__hint" data-lb-hint></span>' +
      '<button type="button" class="cp-lb__close" data-lb-close aria-label="Close">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      "</div>" +
      '<button type="button" class="cp-lb__nav cp-lb__nav--prev" data-lb-prev aria-label="Previous image">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4 7 12l8 8"/></svg></button>' +
      '<button type="button" class="cp-lb__nav cp-lb__nav--next" data-lb-next aria-label="Next image">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 4 8 8-8 8"/></svg></button>';
    root.appendChild(dialog);

    var stage = dialog.querySelector("[data-lb-stage]");
    var img = dialog.querySelector("[data-lb-img]");
    var count = dialog.querySelector("[data-lb-count]");
    var hint = dialog.querySelector("[data-lb-hint]");
    var prevBtn = dialog.querySelector("[data-lb-prev]");
    var nextBtn = dialog.querySelector("[data-lb-next]");
    var finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    if (sources.length < 2) {
      prevBtn.hidden = true;
      nextBtn.hidden = true;
    }

    var index = 0;
    var s = 1;
    var tx = 0;
    var ty = 0;

    /* ── Transform ── */
    function box() {
      return { w: img.offsetWidth, h: img.offsetHeight };
    }

    // Never let a zoomed image pull an edge into view.
    function limit() {
      var b = box();
      tx = clamp(tx, b.w * (1 - s), 0);
      ty = clamp(ty, b.h * (1 - s), 0);
    }

    function apply(animate) {
      img.classList.toggle("is-animating", !!animate);
      img.style.transform = "translate3d(" + tx + "px," + ty + "px,0) scale(" + s + ")";
      dialog.classList.toggle("is-zoomed", s > 1.01);
    }

    function reset(animate) {
      s = 1;
      tx = 0;
      ty = 0;
      apply(animate);
    }

    // Screen point -> point relative to the image's unscaled box.
    function local(clientX, clientY) {
      var r = stage.getBoundingClientRect();
      return { x: clientX - r.left - img.offsetLeft, y: clientY - r.top - img.offsetTop };
    }

    function zoomAt(clientX, clientY, next, animate) {
      var p = local(clientX, clientY);
      var lx = (p.x - tx) / s;
      var ly = (p.y - ty) / s;
      s = clamp(next, 1, MAX);
      tx = p.x - lx * s;
      ty = p.y - ly * s;
      limit();
      apply(animate);
    }

    // Mouse: once zoomed, the image follows the cursor across its whole area.
    function follow(clientX, clientY) {
      var p = local(clientX, clientY);
      var b = box();
      var fx = clamp(p.x / b.w, 0, 1);
      var fy = clamp(p.y / b.h, 0, 1);
      tx = -fx * (s - 1) * b.w;
      ty = -fy * (s - 1) * b.h;
      apply(false);
    }

    /* ── Images ── */
    var loaded = {};

    function preload(i) {
      if (i < 0 || i >= sources.length || loaded[i]) return;
      var probe = new Image();
      probe.decoding = "async";
      probe.src = bigSrc(sources[i]);
      loaded[i] = probe;
    }

    function show(i) {
      index = (i + sources.length) % sources.length;
      var small = sources[index];
      reset(false);
      img.alt = small.alt || "";
      img.src = small.currentSrc || small.src;
      img.classList.remove("is-sharp");

      var big = bigSrc(small);
      var want = index;
      var probe = loaded[index] || new Image();
      loaded[index] = probe;
      var swap = function () {
        if (want !== index || !dialog.open) return;
        img.src = big;
        img.classList.add("is-sharp");
      };
      if (probe.complete && probe.naturalWidth && probe.src === big) swap();
      else {
        probe.onload = swap;
        if (probe.src !== big) probe.src = big;
      }

      count.textContent = sources.length > 1 ? index + 1 + " / " + sources.length : "";
      preload(index + 1);
      preload(index - 1);
    }

    function open(i) {
      show(i);
      hint.textContent = finePointer ? "Click to zoom" : "Pinch or double tap to zoom";
      if (!dialog.open) dialog.showModal();
      document.documentElement.classList.add("cp-modal-open");
    }

    function close() {
      if (dialog.open) dialog.close();
    }

    dialog.addEventListener("close", function () {
      document.documentElement.classList.remove("cp-modal-open");
      reset(false);
    });

    dialog.querySelector("[data-lb-close]").addEventListener("click", close);
    // The dark margin round the photo area (and the empty top bar) closes too.
    dialog.addEventListener("click", function (event) {
      if (event.target === dialog || event.target.classList.contains("cp-lb__bar")) close();
    });
    prevBtn.addEventListener("click", function () { show(index - 1); });
    nextBtn.addEventListener("click", function () { show(index + 1); });

    // Escape is handled here as well as natively: the theme's drawers listen
    // for it on the document and cancel it, which stopped the dialog's own
    // Escape close. Captured on window first, so nothing gets to it before.
    window.addEventListener(
      "keydown",
      function (event) {
        if (!dialog.open) return;
        if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
        else if (event.key === "ArrowLeft") { event.preventDefault(); show(index - 1); }
        else if (event.key === "ArrowRight") { event.preventDefault(); show(index + 1); }
      },
      true
    );
    dialog.addEventListener("cancel", function (event) {
      event.preventDefault();
      close();
    });

    /* ── Mouse ── */
    if (finePointer) {
      stage.addEventListener("click", function (event) {
        // The dark area around the photograph closes it, as people expect.
        if (event.target !== img) {
          close();
          return;
        }
        if (s > 1.01) reset(true);
        else zoomAt(event.clientX, event.clientY, ZOOM, true);
      });
      stage.addEventListener("mousemove", function (event) {
        if (s > 1.01 && !img.classList.contains("is-animating")) follow(event.clientX, event.clientY);
      });
      img.addEventListener("transitionend", function () {
        img.classList.remove("is-animating");
      });
      stage.addEventListener(
        "wheel",
        function (event) {
          event.preventDefault();
          var next = s * (event.deltaY < 0 ? 1.15 : 1 / 1.15);
          if (next < 1.02) reset(false);
          else zoomAt(event.clientX, event.clientY, next, false);
        },
        { passive: false }
      );
    }

    /* ── Touch ── */
    var points = {};
    var gesture = null;
    var lastTap = 0;

    function pointList() {
      return Object.keys(points).map(function (id) { return points[id]; });
    }

    stage.addEventListener("pointerdown", function (event) {
      if (event.pointerType === "mouse") return;
      stage.setPointerCapture(event.pointerId);
      points[event.pointerId] = { x: event.clientX, y: event.clientY };
      var list = pointList();
      if (list.length === 2) {
        var a = list[0], b = list[1];
        gesture = {
          kind: "pinch",
          dist: Math.hypot(a.x - b.x, a.y - b.y),
          s0: s,
          mid: local((a.x + b.x) / 2, (a.y + b.y) / 2),
          tx0: tx,
          ty0: ty
        };
      } else if (list.length === 1) {
        // What the finger landed on is read here: once the stage captures the
        // pointer, pointerup reports the stage as its target, not the photo.
        gesture = { kind: "one", x0: event.clientX, y0: event.clientY, tx0: tx, ty0: ty, t0: Date.now(), moved: false, onImg: event.target === img };
      }
    });

    stage.addEventListener("pointermove", function (event) {
      if (!points[event.pointerId] || !gesture) return;
      points[event.pointerId] = { x: event.clientX, y: event.clientY };
      var list = pointList();

      if (gesture.kind === "pinch" && list.length >= 2) {
        var a = list[0], b = list[1];
        var dist = Math.hypot(a.x - b.x, a.y - b.y);
        var mid = local((a.x + b.x) / 2, (a.y + b.y) / 2);
        var lx = (gesture.mid.x - gesture.tx0) / gesture.s0;
        var ly = (gesture.mid.y - gesture.ty0) / gesture.s0;
        s = clamp(gesture.s0 * (dist / gesture.dist), 1, MAX);
        tx = mid.x - lx * s;
        ty = mid.y - ly * s;
        limit();
        apply(false);
        return;
      }

      if (gesture.kind === "one") {
        var dx = event.clientX - gesture.x0;
        var dy = event.clientY - gesture.y0;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) gesture.moved = true;
        if (s > 1.01) {
          tx = gesture.tx0 + dx;
          ty = gesture.ty0 + dy;
          limit();
          apply(false);
        } else {
          // Unzoomed: the photograph tracks the finger, so the swipe feels
          // physical, and settles or leaves on release.
          img.classList.remove("is-animating");
          var down = Math.max(0, dy);
          var sideways = Math.abs(dx) > Math.abs(dy);
          img.style.transform = sideways
            ? "translate3d(" + dx + "px,0,0)"
            : "translate3d(0," + down + "px,0) scale(" + (1 - Math.min(down, 300) / 1500) + ")";
          dialog.style.setProperty("--lb-fade", sideways ? 1 : 1 - Math.min(down, 300) / 420);
        }
      }
    });

    function endPointer(event) {
      if (!points[event.pointerId]) return;
      delete points[event.pointerId];
      var list = pointList();
      var g = gesture;

      if (g && g.kind === "pinch") {
        if (list.length === 1) {
          // One finger still down after a pinch: carry on as a pan from here.
          gesture = { kind: "one", x0: list[0].x, y0: list[0].y, tx0: tx, ty0: ty, t0: Date.now(), moved: true };
        } else {
          gesture = null;
          if (s < 1.05) reset(true);
        }
        return;
      }
      if (list.length) return;
      gesture = null;
      if (!g) return;

      var dx = event.clientX - g.x0;
      var dy = event.clientY - g.y0;
      dialog.style.setProperty("--lb-fade", 1);

      if (!g.moved) {
        var now = Date.now();
        if (now - lastTap < 300) {
          lastTap = 0;
          if (s > 1.01) reset(true);
          else zoomAt(event.clientX, event.clientY, ZOOM, true);
        } else {
          lastTap = now;
          // A single tap on the dark area closes; on the photo it does nothing,
          // so the first tap of a double tap never closes the viewer.
          if (!g.onImg) {
            window.setTimeout(function () {
              if (lastTap === now) close();
            }, 300);
          }
        }
        return;
      }

      if (s > 1.01) return;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        show(index + (dx < 0 ? 1 : -1));
      } else if (dy > 110 && Math.abs(dy) > Math.abs(dx)) {
        close();
      } else {
        reset(true);
      }
    }

    stage.addEventListener("pointerup", endPointer);
    stage.addEventListener("pointercancel", endPointer);

    /* ── Openers ── */
    // The gallery's mouse drag captures the pointer on the track, so the click
    // that follows lands on the track rather than the image. Which image was
    // pressed is remembered at pointerdown, and a press that moved (a drag)
    // does not open anything.
    var track = root.querySelector("[data-track]");
    var pressed = null;
    if (track) {
      track.addEventListener(
        "pointerdown",
        function (event) {
          var frame = event.target.closest && event.target.closest("[data-frame]");
          pressed = frame ? { frame: frame, x: event.clientX, y: event.clientY } : null;
        },
        true
      );
      track.addEventListener("click", function (event) {
        if (!pressed) return;
        var moved = Math.abs(event.clientX - pressed.x) > 6 || Math.abs(event.clientY - pressed.y) > 6;
        var frameImg = pressed.frame.querySelector("img");
        pressed = null;
        if (moved || !frameImg) return;
        var i = frames.indexOf(frameImg);
        if (i > -1) open(i);
      });
      frames.forEach(function (frameImg, i) {
        frameImg.closest("[data-frame]").setAttribute("data-lb-open", "");
        frameImg.setAttribute("tabindex", "0");
        frameImg.setAttribute("role", "button");
        frameImg.setAttribute("aria-label", "Enlarge image " + (i + 1) + " of " + frames.length);
        frameImg.addEventListener("keydown", function (event) {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            open(i);
          }
        });
      });
    }

    // The whole hero opens it, not just the photograph: on a full-bleed hero
    // the name and the price sit over the photo, so a tap on the picture
    // usually lands on them. Links and buttons in the hero keep their jobs.
    // Opens on the gallery frame showing the same file as the hero.
    var stageEl = hero && hero.closest(".stage");
    if (hero && stageEl) {
      stageEl.setAttribute("data-lb-open", "");
      stageEl.addEventListener("click", function (event) {
        if (event.target.closest("a, button, input, select, label")) return;
        var file = function (node) { return (node.currentSrc || node.src || "").split("?")[0]; };
        var match = 0;
        for (var i = 0; i < frames.length; i += 1) {
          if (file(frames[i]) === file(hero)) { match = i; break; }
        }
        open(match);
      });
    }
  });
})();
